/*
 * @Description: AI Video Editor features section showcasing key features with alternating layout
 */
import Image from 'next/image';
import React from 'react';

import StartButton from './StartButton';

const CDN_BASE = 'https://assets.falcocut.ai/ai-video-editor';

interface FeatureItemProps {
  title: string;
  description: string;
  ctaText: string;
  imageUrl: string;
  imageAlt: string;
  reverse?: boolean;
}

const FeatureItem: React.FC<FeatureItemProps> = ({
  title,
  description,
  ctaText,
  imageUrl,
  imageAlt,
  reverse = false,
}) => {
  return (
    <div
      className={`flex flex-col ${reverse ? 'sm:flex-row-reverse' : 'sm:flex-row'} items-center gap-6 pad:gap-8 sm:gap-12`}
    >
      <div className="flex-1 w-full">
        <div className="rounded-2xl overflow-hidden">
          <Image
            src={imageUrl}
            alt={imageAlt}
            width={540}
            height={304}
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </div>

      <div className="flex-1 w-full flex flex-col items-start">
        <h3 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-gray-900 mb-3 pad:mb-4">
          {title}
        </h3>
        <p className="text-sm pad:text-base sm:text-base text-gray-600 mb-4 pad:mb-6 leading-relaxed">
          {description}
        </p>
        <StartButton text={ctaText} />
      </div>
    </div>
  );
};

const PowerfulFeaturesSection: React.FC = () => {
  const features = [
    {
      title: 'Text\u2011Based Editing',
      description:
        'Tell FalcoCut what you want, and the AI handles the rest. You simply type your instructions\u2014remove text, clean up a scene, adjust visuals\u2014and our video editor applies the edits for you. It\u2019s a fast, intuitive way to update your videos without learning complex tools or timelines, so you can focus on the message instead of the mechanics. You\u2019ll find this especially useful when you need quick edits for social posts, product demos, or last\u2011minute campaign updates.',
      ctaText: 'Edit Your Video',
      imageAlt: 'Text-based video editing feature',
      imageUrl: `${CDN_BASE}/text-based-editing.png`,
    },
    {
      title: 'Give Your Video a Fresh Style',
      description:
        'Refresh the look of your footage using a simple text prompt. FalcoCut can reinterpret your video in a new style, such as cartoon, hand\u2011drawn, cinematic, or colorful artistic looks, while keeping the original movement and pacing intact. It offers a quick way to explore new creative directions, adjust the mood of your content, or prepare alternative versions for different audiences. So you can easily experiment with visual identity without reshooting or rebuilding your material.',
      ctaText: 'Edit Your Video',
      imageUrl: `${CDN_BASE}/give-your-video-a-fresh-style.png`,
      imageAlt: 'Give your video a fresh style feature',
    },
    {
      title: 'Keep Your Original Audio',
      description:
        'Keep the audio you love while editing everything else. With one toggle, FalcoCut protects your original sound so your voice, your music, and your ambience stay exactly as they were recorded. Nothing gets overwritten during the edit, which makes it easy to clean up visuals without changing the way your video sounds. This works especially well for talking\u2011head videos, tutorial clips, and any content where your natural audio is important.',
      ctaText: 'Edit Your Video',
      imageUrl: `${CDN_BASE}/keep-your-original-audio.png`,
      imageAlt: 'Keep your original audio feature',
    },
    {
      title: 'Swap Characters Without Reshoots',
      description:
        'Replace the main character in any video using a simple text command or by uploading a reference photo. FalcoCut analyzes every frame and seamlessly recasts the original person with your chosen character while preserving motion, lighting, and scene continuity. The result is a natural, consistent transformation that feels like it was filmed that way from the start. It\u2019s a powerful way to refresh marketing visuals, test new ad concepts, or generating product\u2011focused e\u2011commerce videos featuring different models or brand ambassadors.',
      ctaText: 'Edit Your Video',
      imageUrl: `${CDN_BASE}/swap-characters-without-reshoots.png`,
      imageAlt: 'Swap characters without reshoots feature',
    },
    {
      title: 'Refine Background with AI',
      description:
        'Transform your background without a studio setup. You can ask FalcoCut to remove a messy backdrop or switch to a cleaner, more professional environment. The editor helps you create videos that look polished and on\u2011brand, whether you\u2019re filming product demos, presentation videos, or social clips. It\u2019s especially useful when you\u2019re recording in tight spaces, busy locations, or any environment that isn\u2019t camera\u2011ready.',
      ctaText: 'Edit Your Video',
      imageUrl: `${CDN_BASE}/refine-background-with-ai.png`,
      imageAlt: 'Refine background with AI feature',
    },
    {
      title: 'Integrate Elements Into Video Naturally',
      description:
        'Enhance your video by adding elements into your video without breaking the flow of the scene. FalcoCut can place product images, icons, or other on\u2011screen elements in a way that aligns with the human subject, making the final result look intentional rather than pasted on. You can highlight key features, showcase products, or add branded touches without worrying about awkward positioning. The effect is most noticeable in product demos, reviews, and any content that relies on added visuals feeling intentional and seamlessly blended into the shot.',
      ctaText: 'Edit Your Video',
      imageUrl: `${CDN_BASE}/integrate-elements-into-video-naturally.png`,
      imageAlt: 'Integrate elements into video naturally feature',
    },
    {
      title: 'Add Text to Video',
      description:
        'Add clear, simple text to your video without needing a separate design tool. FalcoCut can generate basic titles, labels, or short captions from your input and place them directly on the timeline, giving you a quick way to highlight key points or add context. It\u2019s a practical option for drafts, internal updates, onboarding walkthroughs, or any moment where a straightforward text layer helps guide the viewer. While the styling is minimal, it provides a fast foundation you can build on as you refine your edit.',
      ctaText: 'Edit Your Video',
      imageUrl: `${CDN_BASE}/add-text-to-video.png`,
      imageAlt: 'Add text to video feature',
    },
    {
      title: 'Creative Hub for AI Video Workflows',
      description:
        'Teams work faster when everything they need sits in one shared space. FalcoCut brings video creation, editing, voice tools, translation, and creative tools into a unified workspace that supports smooth collaboration. Teammates can review clips, refine drafts, and build on each other\u2019s progress without switching platforms or losing context. This connected environment helps groups move from initial concept to final delivery in a steady, coordinated flow that supports a wide range of content types and creative goals.',
      ctaText: 'Edit Your Video',
      imageUrl: `${CDN_BASE}/creative-hub-for-ai-video-workflows.png`,
      imageAlt: 'Creative hub for AI video workflows feature',
    },
  ];

  return (
    <section className="w-full bg-white py-[90px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-3 pad:mb-4">
          Powerful Features Built for Fast, Simple Editing
        </h2>

        <p className="text-sm pad:text-base text-center text-gray-600 max-w-4xl mx-auto mb-6 pad:mb-8">
          FalcoCut brings smart AI into a clean, easy workflow so anyone can edit videos
          without learning complex software. Every feature is designed to save time,
          reduce effort, and help creators publish more content across every platform.
        </p>

        <div className="space-y-12 pad:space-y-16 sm:space-y-24">
          {features.map((feature, index) => (
            <FeatureItem
              key={index}
              title={feature.title}
              description={feature.description}
              ctaText={feature.ctaText}
              imageUrl={feature.imageUrl}
              imageAlt={feature.imageAlt}
              reverse={index % 2 === 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default PowerfulFeaturesSection;
